import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Result, Space, Spin, Typography } from "antd";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { McpAuthRequestDto, McpRedirectDto } from "@audit-plan/shared";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { fetchSsoConfig, startSso } from "../auth/sso";
import { PolicyGate } from "../components/PolicyGate";
import { palette } from "../theme";
import { BrandBlock } from "./LoginPage";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: palette.bg, padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 480 }}>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <BrandBlock />
          {children}
        </Space>
      </Card>
    </div>
  );
}

/** Consenso: «<client> chiede di accedere ad Audit Plan come <nome> (<ruolo>)» con Consenti / Nega. */
function Consent({ req }: { req: string }) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [done, setDone] = useState(false);
  const info = useQuery({ queryKey: ["mcp-request", req], queryFn: () => api.get<McpAuthRequestDto>("/oauth/mcp/request", { req }), retry: false });
  const decide = useMutation({
    mutationFn: (what: "approve" | "deny") => api.post<McpRedirectDto>(`/oauth/mcp/${what}`, { req }),
    onSuccess: (r) => {
      setDone(true);
      window.location.assign(r.redirectUrl); // torna al client (Claude) con code e state, oppure con access_denied
    },
  });
  if (info.isLoading) return <div style={{ textAlign: "center", padding: 12 }}><Spin /></div>;
  if (info.error || !info.data)
    return <Alert type="error" showIcon message={t("mcp_authErr")} description={info.error instanceof ApiError ? info.error.message : undefined} />;
  const d = info.data;
  if (done) return <Result status="success" title={t("mcp_authDone")} subTitle={t("mcp_authDoneSub")} />;
  return (
    <>
      <Typography.Title level={4} style={{ margin: 0 }}>
        {t("mcp_authAsk", { client: d.clientName, name: me?.name ?? "", role: me ? t(`role_${me.role}`) : "" })}
      </Typography.Title>
      <Typography.Paragraph style={{ margin: 0 }}>{t("mcp_authCan")}</Typography.Paragraph>
      <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
        {t("mcp_authReturn", { host: d.redirectHost })}
      </Typography.Text>
      {d.blocked && <Alert type="warning" showIcon message={d.blocked} />}
      {decide.error && <Alert type="error" showIcon message={decide.error instanceof ApiError ? decide.error.message : t("a_loadFail")} />}
      <Space wrap>
        <Button type="primary" disabled={!!d.blocked} loading={decide.isPending && decide.variables === "approve"} onClick={() => decide.mutate("approve")}>
          {t("mcp_allow")}
        </Button>
        <Button loading={decide.isPending && decide.variables === "deny"} onClick={() => decide.mutate("deny")}>
          {t("mcp_deny")}
        </Button>
      </Space>
    </>
  );
}

/**
 * Pagina di consenso dell'OAuth MCP (AUD-F2.7). /oauth/authorize manda qui il browser con ?req=<id>.
 * Senza sessione dell'app parte l'accesso SSO (returnTo su questa pagina), poi si mostra il consenso.
 */
export default function McpAuthorizePage() {
  const { t } = useTranslation();
  const { me, loading } = useAuth();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const req = sp.get("req") ?? "";
  const here = `${loc.pathname}${loc.search}`;
  const sso = useQuery({ queryKey: ["sso-config"], queryFn: fetchSsoConfig, staleTime: 5 * 60_000, retry: false, enabled: !loading && !me });
  const started = useRef(false);
  useEffect(() => {
    if (me || !sso.data?.enabled || started.current) return;
    started.current = true;
    void startSso(sso.data, here);
  }, [me, sso.data, here]);

  if (!req) return <Shell><Alert type="error" showIcon message={t("mcp_authErr")} /></Shell>;
  if (loading || (!me && sso.isLoading)) return <Shell><div style={{ textAlign: "center", padding: 12 }}><Spin /></div></Shell>;
  if (!me) {
    if (sso.data?.enabled) return <Shell><div style={{ textAlign: "center", padding: 12 }}><Spin /><Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>{t("c_ssoWait")}</Typography.Paragraph></div></Shell>;
    return <Navigate to="/login" state={{ from: here }} replace />; // senza SSO (sviluppo) si entra con le credenziali locali
  }
  if (me.mfa?.setupNeeded) return <Navigate to="/security" replace />;
  return (
    <PolicyGate>
      <Shell>
        <Consent req={req} />
      </Shell>
    </PolicyGate>
  );
}
